import { useEffect, useState } from "react";

/**
 * Installing MetaTrack as an app. Chrome / Edge / Android fire `beforeinstallprompt`
 * once, often before React has started, so it's caught here as soon as the code
 * loads and kept for whenever the Install button is pressed. iPhones and iPads have
 * no such event: the steps are Share → Add to Home Screen, which the app explains.
 */
let deferred = null;
const listeners = new Set();
const notify = () => listeners.forEach((fn) => fn());

if (typeof window !== "undefined") {
  window.addEventListener("beforeinstallprompt", (e) => {
    e.preventDefault();
    deferred = e;
    notify();
  });
  window.addEventListener("appinstalled", () => {
    deferred = null;
    try {
      window.localStorage.setItem("mt_installed", "1");
    } catch {
      /* private browsing */
    }
    notify();
  });
}

export function isStandalone() {
  if (typeof window === "undefined") return false;
  return window.matchMedia?.("(display-mode: standalone)").matches || window.navigator.standalone === true;
}

export function deviceKind() {
  const ua = navigator.userAgent || "";
  const ios = /iPhone|iPad|iPod/.test(ua) || (ua.includes("Macintosh") && navigator.maxTouchPoints > 1);
  if (ios) return "ios";
  if (/Android/.test(ua)) return "android";
  return "desktop";
}

export function useInstall() {
  const [, tick] = useState(0);
  useEffect(() => {
    const fn = () => tick((n) => n + 1);
    listeners.add(fn);
    return () => listeners.delete(fn);
  }, []);
  let installedBefore = false;
  try {
    installedBefore = window.localStorage.getItem("mt_installed") === "1";
  } catch {
    /* private browsing */
  }
  return {
    kind: deviceKind(),
    standalone: isStandalone(),
    installedBefore,
    canPrompt: !!deferred,
    prompt: async () => {
      if (!deferred) return "unavailable";
      const e = deferred;
      deferred = null;
      e.prompt();
      const { outcome } = await e.userChoice;
      notify();
      return outcome;
    },
  };
}
